import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import DatalistSelect from "./DatalistSelect.js";

/**
 * Drop-in replacement for a native <select> on DatalistSelect (searchable +
 * ranked on desktop, bottom sheet on phones). Takes the same <option> /
 * <optgroup> children — or an `options` array of { value, label, sub } — and
 * calls onChange with a select-shaped event ({ target: { name, value } }), so
 * existing handlers keep working.
 *
 *   <SelectPicker name="firmid" value={firmid} onChange={handleChange}>
 *       <option value="">Select firm</option>
 *       {firms.map((f) => <option key={f.firmid} value={f.firmid}>{f.firmname}</option>)}
 *   </SelectPicker>
 *
 * Same id + display-text split as LocationPicker: `value` is the source of
 * truth, the typed text is local, only a real pick (or an exact label) fires
 * onChange, and leaving the field with half-typed text snaps back.
 *
 * value="" options: a placeholder-looking label ("Select …", "-- Choose --") becomes
 * the field's placeholder; any other label ("All", "None", "— No address —") stays a pickable
 * entry. Either way, clearing the text and leaving picks "" — unless the empty
 * option is `disabled` (native: the value can't be cleared once chosen).
 * Disabled options are not offered (native can't pick them either).
 */

const textOf = (node) => {
    if (node == null || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(textOf).join("");
    if (React.isValidElement(node)) return textOf(node.props.children);
    return "";
};

// "Select firm", "-- Choose --", "(select)", "Firm…", "" — but not real choices
// like "— No address —" or "All".
const PLACEHOLDER_RE = /^[\s\-—–(.…]*(?:$|(?:select|choose|pick|please)\b)/i;
const PROMPT_END_RE = /(?:…|\.\.\.)\s*$/;

export const isPlaceholderLabel = (label) => PLACEHOLDER_RE.test(label || "") || PROMPT_END_RE.test(label || "");

/** Flatten <option>/<optgroup> children (through fragments, arrays, conditionals). */
export const optionsFromChildren = (children, group = null, out = []) => {
    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        if (child.type === "option") {
            const label = textOf(child.props.children).replace(/\s+/g, " ").trim();
            const value = child.props.value != null ? String(child.props.value) : label;
            out.push({ value, label, sub: group, disabled: !!child.props.disabled });
        } else if (child.type === "optgroup") {
            optionsFromChildren(child.props.children, child.props.label ?? null, out);
        } else if (child.type === React.Fragment) {
            optionsFromChildren(child.props.children, group, out);
        }
    });
    return out;
};

// Layout properties belong on the wrapper (the flex/grid item), not the input.
const LAYOUT_KEYS = [
    "flex", "flexGrow", "flexShrink", "flexBasis", "alignSelf", "justifySelf", "order",
    "gridColumn", "gridRow", "gridArea",
    "width", "minWidth", "maxWidth",
    "margin", "marginTop", "marginRight", "marginBottom", "marginLeft", "marginInline", "marginBlock",
];

const BLOCK_CLASSES = /\b(form-select|form-control|w-100)\b/;

const SelectPicker = forwardRef(function SelectPicker(
    {
        children,
        options: optionsProp,
        name,
        id,
        value,
        onChange,
        onBlur,
        onFocus,
        onKeyDown,
        onWheel,
        disabled,
        required,
        autoFocus,
        className,
        style,
        placeholder: placeholderProp,
        title,
        "aria-label": ariaLabel,
        sheetTitle,
        searchPlaceholder = "Search…",
        invalidMsg,
        ...rest
    },
    ref
) {
    const all = useMemo(
        () =>
            (optionsProp
                ? optionsProp.map((o) => ({ ...o, value: String(o.value ?? ""), label: String(o.label ?? o.value ?? "") }))
                : optionsFromChildren(children)),
        [optionsProp, children]
    );

    const current = String(value ?? "");
    const emptyOpt = all.find((o) => o.value === "");
    const emptyIsPlaceholder = !!emptyOpt && isPlaceholderLabel(emptyOpt.label);
    const canClear = !!emptyOpt && !emptyOpt.disabled;

    // Offered rows: enabled options, minus a placeholder-style empty one.
    const list = useMemo(
        () =>
            all
                .filter((o) => !o.disabled && !(o.value === "" && emptyIsPlaceholder))
                .map((o) => ({ ...o, __k: o.value })),
        [all, emptyIsPlaceholder]
    );

    const labelFor = (v) => {
        if (v === "" && (!emptyOpt || emptyIsPlaceholder)) return "";
        return all.find((o) => o.value === v)?.label ?? "";
    };
    const currentLabel = labelFor(current);
    const [text, setText] = useState(currentLabel);
    useEffect(() => { setText(currentLabel); }, [currentLabel]);

    const wrapRef = useRef(null);
    // Set on focus; the mouseup that follows a click-to-focus would otherwise
    // drop the select-all and leave the caret mid-word.
    const keepSelectionRef = useRef(false);
    useImperativeHandle(ref, () => ({
        focus: () => inputEl()?.focus(),
        blur: () => inputEl()?.blur(),
        get value() { return current; },
        name,
    }), [current, name]);

    const inputEl = () => wrapRef.current?.querySelector("input:not([aria-hidden])") || null;
    // Select-shaped event. `style`/`dataset` come from the real input so shared
    // handlers (fieldEvents, error helpers) that touch them keep working.
    const makeEvent = (v) => {
        const el = inputEl();
        const target = { name, id, value: v, type: "select-one", style: el?.style || {}, dataset: el?.dataset || {}, element: el };
        return { target, currentTarget: target, persist() {}, preventDefault() {}, stopPropagation() {} };
    };
    const commit = (v) => {
        if (v === current) return;
        onChange?.(makeEvent(v));
    };

    // Split layout props onto the wrapper; the input fills it.
    const inputStyle = {};
    const wrapStyle = {};
    Object.entries(style || {}).forEach(([k, v]) => {
        if (LAYOUT_KEYS.includes(k)) wrapStyle[k] = v;
        else inputStyle[k] = v;
    });
    const isBlock = BLOCK_CLASSES.test(className || "") || wrapStyle.width != null || wrapStyle.flex != null;
    if (!isBlock) {
        // Native selects size to their widest option; do the same (capped).
        const longest = Math.max(placeholderProp?.length || 0, emptyOpt?.label.length || 0, ...all.map((o) => o.label.length), 6);
        wrapStyle.width = `calc(${Math.min(longest, 36)}ch + 44px)`;
    }

    // The global field rules already style a bare input; .form-select would add a
    // second chevron and .form-control greys the phone trigger (readonly).
    const inputClass = (className || "")
        .replace(/\bform-select-sm\b/g, "form-control-sm")
        .replace(/\b(form-select|form-control)\b/g, "")
        .replace(/\s+/g, " ")
        .trim() || undefined;

    const placeholder = placeholderProp ?? (emptyIsPlaceholder ? emptyOpt.label : "");

    return (
        <div
            ref={wrapRef}
            title={title}
            style={{ display: isBlock ? "block" : "inline-block", verticalAlign: "middle", maxWidth: "100%", ...wrapStyle }}
            onKeyDown={() => { keepSelectionRef.current = false; }}
            onMouseUp={(e) => {
                if (keepSelectionRef.current) { keepSelectionRef.current = false; e.preventDefault(); }
            }}
            {...rest}
        >
            <DatalistSelect
                id={id}
                className={inputClass}
                options={list}
                getLabel={(o) => o.label}
                getOptionSub={(o) => o.sub || null}
                idKey="__k"
                inputValue={text}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                autoFocus={autoFocus}
                style={{ ...inputStyle, width: "100%" }}
                sheetTitle={sheetTitle || ariaLabel || title || placeholder || "Select an option"}
                searchPlaceholder={searchPlaceholder}
                invalidMsg={invalidMsg}
                ariaLabel={ariaLabel || title || undefined}
                onChange={(t, k) => {
                    setText(t);
                    if (k != null) commit(String(k));
                }}
                events={{
                    // Like a native select: typing replaces the shown choice.
                    onFocus: (e) => {
                        const el = e.target;
                        if (el && !el.readOnly && typeof el.select === "function") {
                            el.select();
                            keepSelectionRef.current = true;
                        }
                        onFocus?.(e);
                    },
                    // The picker claims the keys it handles (Enter picks/advances,
                    // arrows move the list) by preventDefault; the page sees the rest.
                    onKeyDown: onKeyDown && ((e) => { if (!e.defaultPrevented) onKeyDown(e); }),
                    onWheel,
                    // DatalistSelect's own onBlur skips invalid text, so snap back here.
                    onBlur: (e) => {
                        keepSelectionRef.current = false;
                        const typed = e?.target?.value ?? text;
                        const hit = list.find((o) => o.label === typed);
                        if (hit) {
                            commit(hit.value);
                        } else if (!typed.trim() && canClear) {
                            commit("");
                            setText(labelFor(""));
                        } else {
                            setText(currentLabel);
                        }
                        onBlur?.(makeEvent(hit ? hit.value : !typed.trim() && canClear ? "" : current));
                    },
                }}
            />
        </div>
    );
});

export default SelectPicker;
